window.duplinNativeInventory = window.duplinNativeInventory || (() => {
  const cache = new Map();
  const cacheTtl = 15000;

  const pickupInventoryQuery = `
    query PickupInventory($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          availableForSale
          quantityAvailable
          storeAvailability(first: 50) {
            nodes {
              available
              quantityAvailable
              pickUpTime
              location {
                id
                name
                address {
                  address1
                  address2
                  city
                  provinceCode
                  zip
                }
              }
            }
          }
        }
      }
    }
  `;

  function normalizeVariantId(variantId) {
    const id = String(variantId || '');
    return id.startsWith('gid://shopify/ProductVariant/')
      ? id
      : `gid://shopify/ProductVariant/${id}`;
  }

  function normalizeInventory(variant) {
    if (!variant) return [];
    const onlineQuantity = Number.parseInt(variant.quantityAvailable || 0, 10);
    const shippingQuantity = onlineQuantity > 0 ? onlineQuantity : (variant.availableForSale ? 999999 : 0);
    const shipping = {
      quantity: String(shippingQuantity),
      address: { address1: null, address2: null, address3: '' },
      location: 'Shop location',
      locationId: null,
      pickupHandle: null,
      available: Boolean(variant.availableForSale),
    };
    const pickup = (variant.storeAvailability?.nodes || []).map((availability) => {
      const address = availability.location?.address || {};
      const address3 = [address.city, address.provinceCode, address.zip].filter(Boolean).join(', ');
      const locationId = availability.location?.id || '';
      return {
        quantity: String(Math.max(0, Number.parseInt(availability.quantityAvailable || 0, 10))),
        address: {
          address1: address.address1 || '',
          address2: address.address2 || '',
          address3,
        },
        location: availability.location?.name || '',
        locationId,
        pickupHandle: locationId.split('/').pop() || '',
        available: Boolean(availability.available),
        pickUpTime: availability.pickUpTime || '',
      };
    });
    return [shipping, ...pickup.filter((location) => location.location)];
  }

  async function fetchInventory(variantId, options = {}) {
    const cacheKey = String(variantId || '');
    if (!cacheKey) return [];
    const cached = cache.get(cacheKey);
    if (!options.force && cached && Date.now() - cached.timestamp < cacheTtl) return cached.promise;

    const config = window.duplinStorefrontApi;
    if (!config?.endpoint || !config?.publicAccessToken) {
      throw new Error('Storefront pickup inventory is not configured.');
    }

    const request = fetch(config.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.publicAccessToken,
      },
      body: JSON.stringify({
        query: pickupInventoryQuery,
        variables: { id: normalizeVariantId(cacheKey) },
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Storefront pickup request failed (${response.status})`);
        return response.json();
      })
      .then((result) => {
        if (result.errors?.length) throw new Error(result.errors[0].message || 'Pickup inventory query failed.');
        return normalizeInventory(result.data?.node);
      })
      .catch((error) => {
        cache.delete(cacheKey);
        throw error;
      });

    cache.set(cacheKey, { promise: request, timestamp: Date.now() });
    return request;
  }

  function clear(variantId) {
    if (variantId) cache.delete(String(variantId));
    else cache.clear();
  }

  return { clear, fetch: fetchInventory };
})();

window.duplinPickupGuard = window.duplinPickupGuard || (() => {
  const observedElements = new WeakMap();
  let cartPromise = null;
  let cartFetchedAt = 0;

  function getCookie(name) {
    const cookiePrefix = `${name}=`;
    return decodeURIComponent(document.cookie)
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(cookiePrefix))
      ?.slice(cookiePrefix.length) || '';
  }

  function getRenderedCartItemCount() {
    const countElement = document.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden="true"]');
    const count = Number.parseInt(countElement?.textContent || '0', 10);
    return Number.isNaN(count) ? 0 : count;
  }

  function setProductLocation(location, notify = false) {
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `product_location=${encodeURIComponent(location)};expires=${expires};path=/`;
    if (notify) {
      document.dispatchEvent(
        new CustomEvent('duplin:pickup-location-changed', { detail: { location } })
      );
    }
  }

  function defaultToShippingForEmptyCart(notify = false) {
    if (getRenderedCartItemCount() > 0) return false;
    if (getCookie('product_location') === 'Shop location') return true;
    setProductLocation('Shop location', notify);
    return true;
  }

  function resetToShippingWhenCartEmpties(cartData) {
    if (Number(cartData?.item_count) !== 0) return false;
    if (getCookie('product_location') === 'Shop location') return true;
    setProductLocation('Shop location', true);
    return true;
  }

  function getPickupLocation() {
    const location = getCookie('product_location');
    return location && location !== 'Shop location' ? location : '';
  }

  function shouldSkip(requiresShipping) {
    return requiresShipping === false;
  }

  function getSelectedVariant(element) {
    const productInfo = element?.closest('product-info');
    const selectedVariantElement = productInfo?.querySelector('variant-selects [data-selected-variant]');
    if (selectedVariantElement?.textContent) {
      try {
        return JSON.parse(selectedVariantElement.textContent);
      } catch (_) {
        // Fall through to product-card data.
      }
    }

    const selectedSwatch = element?.closest('.product-card-wrapper')?.querySelector('.visual-swatch.selected');
    if (selectedSwatch?.dataset.variant) {
      try {
        return JSON.parse(selectedSwatch.dataset.variant);
      } catch (_) {
        // Fall through to product-card data.
      }
    }

    const selectedVariantId = element?.querySelector('[name="id"]')?.value;
    const addOnProductId = element?.dataset.addOnProductId;
    const addOnVariantsElement = addOnProductId
      ? document.querySelector(
          `script[data-all-addon-variants][data-add-on-product-id="${addOnProductId}"]`
        )
      : null;
    if (selectedVariantId && addOnVariantsElement?.textContent) {
      try {
        const variants = JSON.parse(addOnVariantsElement.textContent);
        const selectedVariant = variants.find(
          (variant) => String(variant.id) === String(selectedVariantId)
        );
        if (selectedVariant) return selectedVariant;
      } catch (_) {
        // Fall through to rendered product data.
      }
    }

    return null;
  }

  function getProductContext(element) {
    const card = element?.closest('.product-card-wrapper');
    const selectedVariant = getSelectedVariant(element);
    const requiresShippingValue =
      selectedVariant?.requires_shipping ??
      element?.dataset.requiresShipping ??
      card?.dataset.requiresShipping;
    return {
      productType: element?.dataset.productType || card?.dataset.productType || '',
      requiresShipping:
        requiresShippingValue === false || requiresShippingValue === 'false' ? false : true,
      variantTitle: selectedVariant?.title || card?.dataset.variantTitle || '',
    };
  }

  function getAvailableQuantity(locationData, variantTitle) {
    let quantity = Number.parseInt(locationData?.quantity || 0, 10);
    if (String(variantTitle || '').trim().toLowerCase() === 'case') {
      quantity = Math.floor(quantity / 12);
    }
    return Number.isNaN(quantity) ? 0 : Math.max(0, quantity);
  }

  async function getCart(forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && cartPromise && now - cartFetchedAt < 2000) return cartPromise;

    cartFetchedAt = now;
    cartPromise = fetch('/cart.js', { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      })
      .catch((error) => {
        cartPromise = null;
        throw error;
      });
    return cartPromise;
  }

  async function getCartQuantity(variantId, forceRefresh = false) {
    const cart = await getCart(forceRefresh);
    const item = cart.items?.find((cartItem) => String(cartItem.variant_id) === String(variantId));
    return Number.parseInt(item?.quantity || 0, 10) || 0;
  }

  async function check({
    element,
    variantId,
    targetQuantity,
    requestedAddition,
    currentQuantity,
    forceCartRefresh = false,
  }) {
    const location = getPickupLocation();
    const context = getProductContext(element);
    if (!location || !variantId || shouldSkip(context.requiresShipping)) {
      return { shouldCheck: false, available: true, location, availableQuantity: null, targetQuantity: null };
    }

    const resolvedCurrentQuantity = Number.isFinite(Number(currentQuantity))
      ? Number(currentQuantity)
      : await getCartQuantity(variantId, forceCartRefresh);
    const resolvedTargetQuantity = Number.isFinite(Number(targetQuantity))
      ? Number(targetQuantity)
      : resolvedCurrentQuantity + (Number(requestedAddition) || 0);
    const inventory = await window.duplinNativeInventory.fetch(variantId, { force: forceCartRefresh });
    const locationData = inventory.find((item) => item.location === location);
    const availableQuantity = getAvailableQuantity(locationData, context.variantTitle);
    const available = Boolean(locationData) && resolvedTargetQuantity <= availableQuantity;

    let message = '';
    if (!locationData || availableQuantity <= 0) {
      message = `Not available for pickup at ${location}.`;
    } else if (!available) {
      message = resolvedCurrentQuantity >= availableQuantity
        ? `Maximum available quantity reached at ${location}.`
        : `Only ${availableQuantity} available for pickup at ${location}.`;
    }

    return {
      shouldCheck: true,
      available,
      location,
      availableQuantity,
      currentQuantity: resolvedCurrentQuantity,
      targetQuantity: resolvedTargetQuantity,
      message,
    };
  }

  function renderMessage(element, result) {
    if (!element) return;
    let message = element.querySelector(':scope > [data-pickup-inventory-message]');
    if (!result?.shouldCheck || !result.message) {
      message?.remove();
      return;
    }

    if (!message) {
      message = document.createElement('p');
      message.className = 'pickup-inventory-message';
      message.setAttribute('data-pickup-inventory-message', '');
      message.setAttribute('role', 'status');
      element.appendChild(message);
    }

    message.replaceChildren();
    const text = document.createElement('span');
    text.textContent = result.message;
    const link = document.createElement('a');
    link.href = '/cart';
    link.textContent = 'Change pickup location';
    message.append(text, document.createTextNode(' '), link);
  }

  function observe(element, callback) {
    if (!element || observedElements.has(element)) return;
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        observedElements.delete(element);
        callback();
      },
      { rootMargin: '300px 0px' }
    );
    observedElements.set(element, observer);
    observer.observe(element);
  }

  function unobserve(element) {
    observedElements.get(element)?.disconnect();
    observedElements.delete(element);
  }

  function invalidateCart() {
    cartPromise = null;
    cartFetchedAt = 0;
  }

  function getValidationErrorResult() {
    const location = getPickupLocation();
    return {
      shouldCheck: Boolean(location),
      available: !location,
      location,
      availableQuantity: 0,
      message: location ? `Unable to verify pickup availability at ${location}.` : '',
    };
  }

  return {
    check,
    defaultToShippingForEmptyCart,
    getValidationErrorResult,
    getPickupLocation,
    getProductContext,
    invalidateCart,
    observe,
    renderMessage,
    resetToShippingWhenCartEmpties,
    unobserve,
  };
})();

window.duplinPickupCheckout = window.duplinPickupCheckout || (() => {
  const preferenceMutation = `
    mutation SetDeliveryPreference($cartId: ID!, $deliveryMethod: [PreferenceDeliveryMethodType!], $pickupHandle: [String!]) {
      cartBuyerIdentityUpdate(
        cartId: $cartId
        buyerIdentity: {
          preferences: {
            delivery: {
              deliveryMethod: $deliveryMethod
              pickupHandle: $pickupHandle
            }
          }
        }
      ) {
        cart { id }
        userErrors { message }
      }
    }
  `;
  let checkoutPending = false;

  function shouldSkip(item) {
    return item?.requires_shipping === false;
  }

  function getAdjustedQuantity(locationData, variantTitle) {
    let quantity = Number.parseInt(locationData?.quantity || 0, 10);
    if (String(variantTitle || '').trim().toLowerCase() === 'case') quantity = Math.floor(quantity / 12);
    return Number.isNaN(quantity) ? 0 : Math.max(0, quantity);
  }

  async function getCart() {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  async function validateCart(cart, options = {}) {
    const location = window.duplinPickupGuard?.getPickupLocation() || '';
    if (!location) return { valid: true, location: '', pickupHandle: '', errors: [] };

    const eligibleItems = (cart?.items || []).filter((item) => !shouldSkip(item));
    const checks = await Promise.all(eligibleItems.map(async (item) => {
      const inventory = await window.duplinNativeInventory.fetch(item.variant_id, { force: options.force });
      const locationData = inventory.find((entry) => entry.location === location);
      const availableQuantity = getAdjustedQuantity(locationData, item.variant_title);
      return {
        item,
        locationData,
        availableQuantity,
        valid: Boolean(locationData) && item.quantity <= availableQuantity,
      };
    }));
    const errors = checks.filter((check) => !check.valid);
    const pickupHandle = checks.find((check) => check.locationData?.pickupHandle)?.locationData?.pickupHandle || '';
    return { valid: errors.length === 0 && Boolean(pickupHandle), location, pickupHandle, errors, checks };
  }

  function renderNotice(result) {
    document.querySelectorAll('[data-pickup-cart-notice]').forEach((notice) => notice.remove());
    if (result?.valid !== false) return;
    const message = document.createElement('p');
    message.setAttribute('data-pickup-cart-notice', '');
    message.setAttribute('role', 'alert');
    message.className = 'cart__pickup-inventory-error';
    message.textContent = `Some items are not available in the requested quantity for pickup at ${result.location}. Reduce the quantity, choose another location, or switch to shipping.`;
    const targets = document.querySelectorAll('.cart__ctas');
    targets.forEach((target, index) => {
      target.parentNode?.insertBefore(index === 0 ? message : message.cloneNode(true), target);
    });
  }

  function setCheckoutDisabled(disabled) {
    document.querySelectorAll('button[name="checkout"], .cart__checkout-button').forEach((button) => {
      if (disabled) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.checkoutPending !== 'true') {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
      }
    });
  }

  async function setDeliveryPreference(cart, validation) {
    const config = window.duplinStorefrontApi;
    if (!config?.endpoint || !config?.publicAccessToken || !cart?.token) {
      throw new Error('Checkout delivery preference is not configured.');
    }
    const isPickup = Boolean(validation.location);
    const cartId = String(cart.token).startsWith('gid://shopify/Cart/')
      ? String(cart.token)
      : `gid://shopify/Cart/${cart.token}`;
    const response = await fetch(config.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.publicAccessToken,
      },
      body: JSON.stringify({
        query: preferenceMutation,
        variables: {
          cartId,
          deliveryMethod: [isPickup ? 'PICK_UP' : 'SHIPPING'],
          pickupHandle: isPickup ? [validation.pickupHandle] : [],
        },
      }),
    });
    const result = await response.json();
    const userErrors = result.data?.cartBuyerIdentityUpdate?.userErrors || [];
    if (!response.ok || result.errors?.length || userErrors.length || !result.data?.cartBuyerIdentityUpdate?.cart) {
      throw new Error(userErrors[0]?.message || result.errors?.[0]?.message || 'Unable to save checkout delivery preference.');
    }
  }

  async function prepare() {
    const cart = await getCart();
    const validation = await validateCart(cart, { force: true });
    renderNotice(validation);
    if (!validation.valid) {
      setCheckoutDisabled(true);
      return false;
    }
    await setDeliveryPreference(cart, validation);
    return true;
  }

  async function handleCheckout(event) {
    const button = event.target.closest('button[name="checkout"]');
    if (!button || button.dataset.checkoutPrepared === 'true') return;
    if (checkoutPending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutPending = true;
    button.dataset.checkoutPending = 'true';
    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-disabled', 'true');
    try {
      const cartUiValid = typeof window.cartValidate === 'function' ? await window.cartValidate(true) : true;
      const prepared = cartUiValid !== false && await prepare();
      if (!prepared) return;
      button.dataset.checkoutPrepared = 'true';
      window.location.assign('/checkout');
    } catch (error) {
      console.error('Unable to prepare pickup checkout:', error);
      renderNotice({ valid: false, location: window.duplinPickupGuard?.getPickupLocation() || 'the selected location' });
      setCheckoutDisabled(true);
    } finally {
      checkoutPending = false;
      delete button.dataset.checkoutPending;
      if (button.dataset.checkoutPrepared !== 'true' && !button.disabled) button.removeAttribute('aria-disabled');
    }
  }

  document.addEventListener('click', handleCheckout, true);
  return { prepare, renderNotice, setCheckoutDisabled, setDeliveryPreference, validateCart };
})();

window.duplinPickupGuard.defaultToShippingForEmptyCart();
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    window.duplinPickupGuard.resetToShippingWhenCartEmpties(event?.cartData);
  });
}
